import type { FxParam } from "./fxBus";
import type { EnvelopeParam } from "./envelope";
import type { DrumId } from "../constants/drums";
import type {
  Pattern,
  MelodyGrid,
  FxState,
  TrackMutes,
  DrumMutes,
} from "../state/instrumentStore";
import type { KeyboardTrackId, PitchClass, TrackId } from "../constants/scale";
import { buildScale } from "../constants/scale";
import { DRUMS } from "../constants/drums";

const ALL_TRACKS: readonly TrackId[] = ["drums", "keys", "bass", "lead"];

export type FxOrEnvelopeParam = FxParam | EnvelopeParam;

export interface StepPlayer {
  triggerDrum(id: DrumId, time: number): void;
  setMelodyChordAt(
    track: KeyboardTrackId,
    midis: readonly number[],
    time: number,
  ): void;
  setBpm(bpm: number): void;
  setFx(track: TrackId, param: FxOrEnvelopeParam, value01: number): void;
}

export interface StepSlice {
  pattern: Pattern;
  melodyGrids: Record<KeyboardTrackId, MelodyGrid>;
  fx: Record<TrackId, FxState>;
  trackMutes: TrackMutes;
  drumMutes: DrumMutes;
  rootPitch: PitchClass;
  blueFlat3: boolean;
  blueFlat5: boolean;
  bpm: number;
}

/**
 * Emit drum hits for a single step of the drums track. One hit per armed cell.
 * Safe to call at any Tone-scheduled time.
 */
export function scheduleDrumStep(
  engine: StepPlayer,
  slice: StepSlice,
  step: number,
  time: number,
): void {
  if (slice.trackMutes.drums) return;
  const { pattern, drumMutes } = slice;
  for (let row = 0; row < pattern.length; row++) {
    const drum = DRUMS[row];
    if (!drum) continue;
    const cell = pattern[row]?.[step];
    if (!cell || drumMutes[drum.id]) continue;
    engine.triggerDrum(drum.id, time);
  }
}

/**
 * Update the melodic voice for a single track at a single step. The chord is
 * derived from every armed row in the column (`keys` is polyphonic, `bass` /
 * `lead` stop at the first armed row). Sustain is preserved across consecutive
 * armed cells by way of the engine's held-midi bookkeeping.
 */
export function scheduleMelodyStep(
  engine: StepPlayer,
  slice: StepSlice,
  track: KeyboardTrackId,
  step: number,
  time: number,
): void {
  if (slice.trackMutes[track]) {
    engine.setMelodyChordAt(track, [], time);
    return;
  }
  const grid = slice.melodyGrids[track];
  const scale = buildScale(track, {
    root: slice.rootPitch,
    blueFlat3: slice.blueFlat3,
    blueFlat5: slice.blueFlat5,
  });
  const midis: number[] = [];
  for (let r = 0; r < grid.length && r < scale.length; r++) {
    if (grid[r]?.[step]) {
      midis.push(scale[r].midi);
      if (track !== "keys") break;
    }
  }
  engine.setMelodyChordAt(track, midis, time);
}

/**
 * Apply non-step-level settings (BPM + FX) that change at segment boundaries.
 */
export function applySegmentSettings(
  engine: StepPlayer,
  slice: StepSlice,
): void {
  engine.setBpm(slice.bpm);
  for (const track of ALL_TRACKS) {
    const fx = slice.fx[track];
    engine.setFx(track, "volume", fx.volume);
    engine.setFx(track, "fuzz", fx.fuzz);
    engine.setFx(track, "delay", fx.delay);
    engine.setFx(track, "reverb", fx.reverb);
    engine.setFx(track, "decay", fx.decay);
  }
}
